import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import {
    detailCta,
    DetailTileCard,
    type DetailTile,
} from '~/ui-rocco/pages/detail-shared';
import { getSelectedShowcase } from '../utils/featured-work';
import { NAV_DARK_GROUND } from '../hooks/use-band-over-dark';
import { PROFILE_TILE_TEXT } from './professional-tile-props';

/**
 * §5 — (01) Featured work. The professional's featured projects as a 3-column
 * grid (per the Figma, not a carousel) with a View-all-Projects link. Tiles
 * reuse the shared {@link DetailTileCard} (Save overlay + per-tile BM admin
 * menu); the landscape media aspect overrides the card's default portrait.
 * Product suppliers (e.g. Resene) have no projects of their own, so the grid
 * showcases their featured products and relabels accordingly.
 */
interface ProfessionalSelectedWorkSectionProps {
    sectionNumber?: number;
}

export const ProfessionalSelectedWorkSection = ({
    sectionNumber,
}: ProfessionalSelectedWorkSectionProps = {}) => {
    const { featuredProjects, featuredProducts, professionalOverview } =
        useLoaderData<typeof loader>();

    const { usingProducts, items } = getSelectedShowcase(
        featuredProjects.featuredProjectsList,
        featuredProducts.featuredProductsList,
        Boolean(professionalOverview.IsManufacturer),
    );
    // Two full rows either way: projects run 3-up (6), products 4-up (8).
    const work = items.slice(0, usingProducts ? 8 : 6);

    const adminDataMap = useItemAdminData(work.map((item) => item.id));

    if (work.length === 0) return null;

    const tiles: DetailTile[] = work.map((item) => ({
        id: item.id,
        link: item.link,
        title: item.title,
        images: item.images,
        subtitle: item.professional?.name,
        professional: {
            id: item.professional?.id,
            name: item.professional?.name,
        },
        pinType: usingProducts ? 'ProductPin' : 'ProjectPin',
        adminData: adminDataMap[item.id],
        professionalLink: professionalOverview.Link,
        website: professionalOverview.Website,
    }));

    return (
        <section
            {...NAV_DARK_GROUND}
            className="w-full bg-[#181818] text-white focus-ring-on-dark max-md:px-6 max-md:py-12 md:px-[3.125rem] md:pt-[6.25rem] md:pb-[10rem]"
        >
            {/* The page's own lane: 50px margins and the 120rem cap every
                other section uses. At 37px and 115.4rem the featured tiles ran
                wider than everything above and below them. */}
            <div className="mx-auto w-full max-w-[120rem]">
                {/* Mobile keeps its heading; desktop opens straight on the
                    work. The numbered rule that used to sit here said the
                    section's name to a reader already looking at it. */}
                <div className="md:hidden">
                    <h2 className="m-0 font-sans font-normal text-white max-md:mt-4 max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em]">
                        {usingProducts
                            ? `Featured Products by ${professionalOverview.Title}`
                            : `Featured Projects by ${professionalOverview.Title}`}
                    </h2>
                </div>

                {/* Projects read as landscape editorial tiles, 3-up; products
                    mirror the Products tab's portrait tiles, 4-up (2-up on
                    mobile, like that tab's grid). */}
                <div
                    className={cn(
                        'grid max-md:pt-8 md:gap-x-3 md:gap-y-[6.25rem] md:pt-[3rem]',
                        usingProducts
                            ? 'grid-cols-2 max-md:gap-x-2 max-md:gap-y-8 md:grid-cols-4'
                            : 'grid-cols-1 max-md:gap-6 md:grid-cols-3',
                    )}
                >
                    {tiles.map((tile) => (
                        <DetailTileCard
                            key={tile.id}
                            tile={tile}
                            theme="dark"
                            textClassName={PROFILE_TILE_TEXT}
                            mediaClassName={cn(
                                'bg-neutral-800',
                                usingProducts
                                    ? 'max-md:aspect-[437/524] md:aspect-[437/524]'
                                    : 'max-md:aspect-[592/430] md:aspect-[592/430]',
                            )}
                        />
                    ))}
                </div>

                {/* View all — centred below the grid (Figma §5). */}
                <div className="flex justify-center max-md:pt-8 md:pt-[6.25rem]">
                    <RoccoLink
                        to={`${professionalOverview.Link}/${
                            usingProducts ? 'our-products' : 'case-studies'
                        }`}
                        className={detailCta('grey-dark')}
                    >
                        {usingProducts
                            ? 'View all Products'
                            : 'View all Projects'}
                    </RoccoLink>
                </div>
            </div>
        </section>
    );
};
